 
/* eslint-disable no-param-reassign */
/* eslint-disable no-underscore-dangle */
 
const Backbone = require('lib/backbone')
const _ = require('lib/underscore')
const g = require('globals')
const log = require('log').create('base model')
require('backbone-associations')
Backbone.Associations.EVENTS_NC = true // allow for nested-change events in associated models

// copied from Backbone.Associations
// Tokenize the fully qualified event path
function getPathArray(path) {
    if (path === '') return ['']
    return _.isString(path) ? path.split('.') : path || []
}


const BaseModel = Backbone.AssociatedModel.extend({

    /*
    BaseModel can set and get calculated values just like regular values
    e.g. this.set('display_name', function() {
        return this.get('first_name') + ' ' + this.get('last_name');
    });

    Once defined, calculated values can be accessed just like regular values
    e.g. this.get('display_name');

    You can define calculated values in the defaults hash of your subclass.
    See module/user/user for an example.

    cf. http://kilon.org/blog/2012/02/backbone-calculated-fields/
    */

    initialize(attrs = {}, options = {}) {
        // inherit superclass initialize
        Backbone.AssociatedModel.prototype.initialize.apply(this, arguments)

        _.defaults(options, {
            batch_delay: 2000,
        })
        this.options = options

        _.bindAll(this, 'submit_batch')
        this.submit_batch = _.debounce(this.submit_batch, options.batch_delay)
        this.patches = {}

        this.calculated_values = _.extend(
            {},
            this.calculated_values,
            attrs.calculated_values,
            options.calculated_values
        )
        delete this.attributes.calculated_values

        this.validators = _.extend({}, this.validators, options.validators)
    },

    /*
    BaseModel provides per-attribute validation instead of defining all
    validation in the validate method, subclasses can define a validator for
    each attribute in the validators hash, using the attribute name as the key
    subclasses can opt out of this by overriding the validate method (which
    means that existing subclass validation will work as is)
    */
    validate(attrs) {
        return this.validateAttributes(attrs)
    },

    validateAttributes(attrs) {
        let invalid
        Object.keys(attrs).forEach(key => {
            if (invalid) return // only return first error
            invalid = this.validateAttribute(key, attrs[key], attrs)
        })
        return invalid
    },

    validateAttribute(key, val, attrs) {
        const validator = this.validators && this.validators[key]
        if (_.isFunction(validator)) {
            return validator.call(this, val, attrs)
        }
    },

    validators: {},

    // helper method so other methods can more easily handle both `"key", value`
    // and `{key: value}` -style arguments.
    parseKeyValArguments(key, val, options) {
        let attrs

        if (typeof key === 'object') {
            attrs = key
            options = val || {}
        } else {
            (attrs = {})[key] = val
            options = options || {}
        }

        return {
            attrs,
            options,
        }
    },

    // helper method to call save with patch: true
    patch() {
        let { attrs, options } = this.parseKeyValArguments.apply(this, arguments)

        options.patch = true

        return this.save.apply(this, [attrs, options])
    },

    // batch save method
    batch() {
        let { attrs, options } = this.parseKeyValArguments.apply(this, arguments)

        // update patches with new attributes
        _.extend(this.patches, attrs)

        // call the debounced submit method
        this.submit_batch()

        // set the attributes so the UI updates immediately
        return this.set.apply(this, [attrs, options])
    },

    submit_batch() {
        this.patch(_.clone(this.patches))
        this.patches = {}
    },

    sync(method, model, options = {}) {

        // this is content type application/x-www-form-urlencoded (TurboGears)
        if (_.isString(this.url) && this.url.substring(0, 5) === '/ajax') {
            options.emulateJSON = true

            _.defaults(options, {
                type: options.type || 'POST',
                success(output) {
                    log('There was generic ajax success.', output)
                },
                error(output) {
                    log('There was a generic ajax error.', output)
                },
                data: model.toJSON(),
            })

            options.data._authentication_token = g.AUTH_TOKEN
        }

        // log('sync method', _.result(this, 'url'), method, model, options)

        return Backbone.AssociatedModel.prototype.sync.apply(this, arguments)
    },

    // override Backbone.Associations _getAttr method
    // Navigate the path to the leaf object in the path to query for the attribute value
    _getAttr(path) {
        let result = this
        const attrs = getPathArray(path)
        if (! attrs.length) return

        while (attrs.length) {
            const key = attrs.shift()
            if (! result) break
            // Navigate the path to get to the result
            result = (
                result instanceof Backbone.Collection
                    ? (isNaN(key) ? undefined : result.at(key))
                    : result._getModelAttr(key)
            )
        }
        return result
    },

    _getModelAttr(key) {
        if (this.calculated_values && this.calculated_values[key]) {
            return this.calculated_values[key].call(this)
        }

        return this.attributes[key]
    },

    get(key) {
        if (this.calculated_values && this.calculated_values[key]) {
            return this.calculated_values[key].call(this)
        }

        return Backbone.AssociatedModel.prototype.get.apply(this, arguments)
    },

    // override set to check for calculated_values in set attrs
    set() {
        let { attrs, options } = this.parseKeyValArguments.apply(this, arguments)

        if (attrs.calculated_values) {
            _.extend(this.calculated_values, attrs.calculated_values)
            delete attrs.calculated_values
        }

        return Backbone.AssociatedModel.prototype.set.apply(this, [attrs, options])
    },

    // BaseModel.is does an explicit true/false check on the given key
    // e.g. Viewer.is('admin') is the same as doing: Viewer.get('admin') === true
    is() {
        const value = this.get.apply(this, arguments)
        return (value === true)
    },
    isNot() {
        const value = this.get.apply(this, arguments)
        return (value !== true)
    },

})

module.exports = BaseModel
